import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  UkBboxCoordinates,
  listPaymentMethodsUrl,
  printersCodeUrl,
  printersLocationsUrl,
  shopsLocationsUrl,
  stripePaymentIntentUrl,
} from '../../../settings';
import { getStateAndToken } from '../../../utils/helpers';
import { sendRequest } from '../../api';

export const getShopsLocation = createAsyncThunk('app/getShopsLocation', async (_, thunkApi) => {
  try {
    const [state, access_token] = getStateAndToken(thunkApi);
    const { data } = await sendRequest(
      'get',
      `${shopsLocationsUrl}&tl_lat=${UkBboxCoordinates[1]}&tl_lon=${UkBboxCoordinates[0]}&br_lat=${UkBboxCoordinates[3]}&br_lon=${UkBboxCoordinates[2]}`,
      null,
      `Bearer ${access_token}`,
    );

    return data;
  } catch (error: any) {
    const errorMessage = error.response ? error.response.data.message : error.message;
    return thunkApi.rejectWithValue(errorMessage);
  }
});

export const getPrintersLocation = createAsyncThunk('app/getPrintersLocation', async (_, thunkApi) => {
  try {
    const [state, access_token] = getStateAndToken(thunkApi);
    const { data } = await sendRequest(
      'get',
      `${printersLocationsUrl}&tl_lat=${UkBboxCoordinates[1]}&tl_lon=${UkBboxCoordinates[0]}&br_lat=${UkBboxCoordinates[3]}&br_lon=${UkBboxCoordinates[2]}`,
      null,
      `Bearer ${access_token}`,
    );

    return data;
  } catch (error: any) {
    const errorMessage = error.response ? error.response.data.message : error.message;
    return thunkApi.rejectWithValue(errorMessage);
  }
});

export const createPaymentIntent = createAsyncThunk(
  'app/createPaymentIntent',
  async ({ order_id, service_type }: { order_id: string; service_type: string }, thunkApi) => {
    try {
      const [state, access_token] = getStateAndToken(thunkApi);
      const body = { service_type, order_id };

      const { data } = await sendRequest('post', stripePaymentIntentUrl, body, `Bearer ${access_token}`);

      return data;
    } catch (error: any) {
      const errorMessage = error.response ? error.response.data.message : error.message;
      return thunkApi.rejectWithValue(errorMessage);
    }
  },
);

export const getPaymentList = createAsyncThunk('app/getPaymentList', async (_, thunkApi) => {
  try {
    const [state, access_token] = getStateAndToken(thunkApi);
    const { service_type } = state.order;
    const body = {
      service_type:
        service_type === 'click_and_collect' ? 'collect' : service_type === 'delivery' ? 'delivery' : 'printt_now',
    };
    const { data } = await sendRequest('post', listPaymentMethodsUrl, body, `Bearer ${access_token}`);

    return data;
  } catch (error: any) {
    const errorMessage = error.response ? error.response.data.message : error.message;
    return thunkApi.rejectWithValue(errorMessage);
  }
});

// The Self Service location (shop) a scanned printer belongs to. The map only loads the printers around the UK, so
// one outside it (e.g. a test printer abroad) is looked up in the full list
export const getPrinterLocation = createAsyncThunk(
  'app/getPrinterLocation',
  async ({ locationId }: { locationId: number }, thunkApi) => {
    try {
      const [state, access_token] = getStateAndToken(thunkApi);
      const loaded = (state.app.printers ?? []).find((shop: any) => Number(shop.id) === locationId);
      if (loaded) return loaded;
      const { data } = await sendRequest(
        'get',
        `${printersLocationsUrl}&tl_lat=85&tl_lon=-180&br_lat=-85&br_lon=180`,
        null,
        `Bearer ${access_token}`,
      );
      return (data ?? []).find((shop: any) => Number(shop.id) === locationId) ?? null;
    } catch (error: any) {
      const errorMessage = error.response ? error.response.data.message : error.message;
      return thunkApi.rejectWithValue(errorMessage);
    }
  },
);

export const handlePrinterScanCode = createAsyncThunk(
  'app/handlePrinterScanCode',
  async ({ selectedPrinter }: { selectedPrinter: any }, thunkApi) => {
    try {
      const [state, access_token] = getStateAndToken(thunkApi);
      // Printer codes are capitals and digits (the API rejects anything else), however they were typed or scanned
      const code = String(selectedPrinter ?? '')
        .trim()
        .toUpperCase();
      const body = {
        text: code,
      };
      const { data } = await sendRequest('get', `${printersCodeUrl}/${code}`, body, `Bearer ${access_token}`);
      return data;
    } catch (error: any) {
      const errorMessage = error.response ? error.response.data.message : error.message;
      return thunkApi.rejectWithValue(errorMessage);
    }
  },
);
